import { CollapsibleSection } from '@strata/react-collapsible-section';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DialogWithForm } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { resubmitClaim } from 'api/medbillingAPI';
import { ResubmitClaimServices } from 'components/claims/dialogs/ResubmitClaimServices';
import { handleError } from 'services/Error';
import {
    ClaimFrequencyTypeOptions,
    ClaimFrequencyTypes,
    ClaimPlaceOfServiceMap,
    ClaimPlaceOfServiceOptions,
    ClaimPlaceOfServiceReferralPlaceOfServiceMap,
    SubmissionTypes,
} from 'services/MedicalBillingConstants';
import { AppRoutes } from 'services/routes';
import { type ReferralDetails } from 'store/api/clientApi';
import { useGetClaimDetailsQuery, useRefetchAllClaimDetails } from 'store/api/medbillingApi';
import { useGetDiagnosisCodeDescriptionMapQuery } from 'store/api/referenceApi';

const Title = 'Resubmit Claim';

const ReasonOptions = [
    'Denied Claim',
    'No 999 Received',
    'Payer Issue',
    'Processing Issue',
    'Rejected 277 CA',
    'Rejected 999',
];

const ValidationSchema = Yup.object({
    originalClaimNumber: Yup.string(),
    diagnoses: Yup.array()
        .of(Yup.string().required())
        .required(validationStrings.requiredField.errorMessage)
        .min(1, validationStrings.requiredField.errorMessage),
    placeOfService: Yup.string()
        .oneOf(Object.keys(ClaimPlaceOfServiceMap))
        .required(validationStrings.requiredField.errorMessage),
    claimFrequencyType: Yup.string()
        .oneOf(Object.values(ClaimFrequencyTypes))
        .required(validationStrings.requiredField.errorMessage),
    memberId: Yup.string().required(validationStrings.requiredField.errorMessage),
    ...ResubmitClaimServices.validationSchema,
    ...ReasonInput.validationSchema,
    // Non-editable fields
    referralId: Yup.string().required(validationStrings.requiredField.errorMessage),
    claimId: Yup.string().required(validationStrings.requiredField.errorMessage),
    supplierNpi: Yup.string().required(validationStrings.requiredField.errorMessage),
    patientAccountNumber: Yup.string().required(validationStrings.requiredField.errorMessage),
    providerSignatureOnFile: Yup.string().required(validationStrings.requiredField.errorMessage),
    providerAcceptAssignment: Yup.string().required(validationStrings.requiredField.errorMessage),
    assignmentOfBenefits: Yup.string().required(validationStrings.requiredField.errorMessage),
    releaseOfInformation: Yup.string().required(validationStrings.requiredField.errorMessage),
    submissionType: Yup.string()
        .oneOf(Object.values(SubmissionTypes))
        .required(validationStrings.requiredField.errorMessage),
    taxonomyCode: Yup.string().required(validationStrings.requiredField.errorMessage),
});

interface ResubmitClaimDialogProps {
    onClose: () => void;
    payerId: string;
    transactionId: string;
    referralDetails?: ReferralDetails;
}

export const ResubmitClaimDialog = ({
    onClose,
    payerId,
    transactionId,
    referralDetails = undefined,
}: ResubmitClaimDialogProps) => {
    const navigate = useNavigate();

    const { data: claimDetails } = useGetClaimDetailsQuery({
        payerId,
        transactionId,
    });

    const { data: diagnosisCodeDescriptionMap, isLoading: isDiagnosisCodeDescriptionMapLoading } =
        useGetDiagnosisCodeDescriptionMapQuery();
    const diagnosisCodeOptions = useMemo(
        () =>
            Object.entries(diagnosisCodeDescriptionMap ?? {}).map(([key, value]) => ({
                name: value,
                secondaryData: key,
                code: key,
            })),
        [diagnosisCodeDescriptionMap],
    );

    const refetchAllClaimDetails = useRefetchAllClaimDetails({
        payerId,
        transactionId,
    });

    return (
        <Formik
            initialValues={{ ...claimDetails } as Yup.InferType<typeof ValidationSchema>}
            enableReinitialize
            validateOnMount
            validationSchema={ValidationSchema}
            onSubmit={async (values, { resetForm }) => {
                try {
                    const resubmittedTransactionId = await resubmitClaim({
                        transactionId,
                        payerId,
                        claim: {
                            original_claim_number: values.originalClaimNumber,
                            diagnoses: values.diagnoses,
                            place_of_service: values.placeOfService,
                            claim_frequency_type: values.claimFrequencyType,
                            member_id: values.memberId,
                            // Non-editable fields
                            payer_id: payerId,
                            referral_id: values.referralId,
                            claim_id: values.claimId,
                            friendly_id: referralDetails?.friendlyId,
                            supplier_npi: values.supplierNpi,
                            patient_account_number: values.patientAccountNumber,
                            provider_signature_on_file: values.providerSignatureOnFile,
                            provider_accept_assignment: values.providerAcceptAssignment,
                            assignment_of_benefits: values.assignmentOfBenefits,
                            release_of_information: values.releaseOfInformation,
                            submission_type: values.submissionType,
                            taxonomy_code: values.taxonomyCode,
                            ...ResubmitClaimServices.submitValues(values),
                        },
                        ...ReasonInput.submitValues(values),
                    });

                    refetchAllClaimDetails();

                    SuccessToast({ message: 'Claim resubmitted.' });
                    resetForm();
                    onClose();

                    navigate(AppRoutes.claimDetails(payerId, resubmittedTransactionId));
                } catch (err) {
                    handleError('Unable to resubmit claim. Please try again.', err as Error);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={560}
                    isOpen
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Resubmit Claim"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <div className="sds_update-claim-dialog">
                            <div className="padding__bottom--medium">
                                <CollapsibleSection
                                    title="Claim Data"
                                    renderExpanded={false}
                                    content={
                                        <>
                                            <TextInputFormik
                                                id="originalClaimNumber"
                                                label="Payer Claim ID"
                                            />
                                            <SuggestMultiSelectFormik
                                                id="diagnoses"
                                                label="Diagnoses"
                                                required
                                                isLoading={isDiagnosisCodeDescriptionMapLoading}
                                                values={diagnosisCodeOptions}
                                                selectedItems={diagnosisCodeOptions.filter(
                                                    ({ code }) =>
                                                        formik.values.diagnoses?.includes(code),
                                                )}
                                                onChange={(newValues) => {
                                                    void formik.setFieldValue(
                                                        'diagnoses',
                                                        newValues?.map(({ code }) => code),
                                                    );
                                                }}
                                            />
                                            <SuggestSingleSelectFormik
                                                id="placeOfService"
                                                label="Place Of Service"
                                                required
                                                values={ClaimPlaceOfServiceOptions}
                                                selectedItem={ClaimPlaceOfServiceOptions?.find(
                                                    ({ value }) =>
                                                        value === formik.values.placeOfService,
                                                )}
                                                onChange={(newVal) => {
                                                    void formik.setFieldValue(
                                                        'placeOfService',
                                                        newVal?.value,
                                                    );
                                                }}
                                            />
                                            {formik.values.placeOfService &&
                                                ClaimPlaceOfServiceReferralPlaceOfServiceMap[
                                                    formik.values
                                                        .placeOfService as keyof typeof ClaimPlaceOfServiceReferralPlaceOfServiceMap
                                                ] !== referralDetails?.placeOfService && (
                                                    <div className="margin__bottom--medium">
                                                        <InlineMessage
                                                            wrapText
                                                            intent={InlineMessage.Intent.WARNING}
                                                            label="The selected place of service does not match this referral. The place of service determines the rate at which the drug will be billed."
                                                        />
                                                    </div>
                                                )}
                                            <SuggestSingleSelectFormik
                                                id="claimFrequencyType"
                                                label="Claim Frequency"
                                                required
                                                values={ClaimFrequencyTypeOptions}
                                                selectedItem={ClaimFrequencyTypeOptions?.find(
                                                    ({ value }) =>
                                                        value === formik.values.claimFrequencyType,
                                                )}
                                                onChange={(newVal) => {
                                                    void formik.setFieldValue(
                                                        'claimFrequencyType',
                                                        newVal?.value,
                                                    );
                                                }}
                                            />
                                            <TextInputFormik
                                                id="memberId"
                                                label="Member ID"
                                                required
                                            />
                                        </>
                                    }
                                />
                            </div>
                            <ResubmitClaimServices
                                diagnosisCodeOptions={diagnosisCodeOptions}
                                isDiagnosisCodeDescriptionMapLoading={
                                    isDiagnosisCodeDescriptionMapLoading
                                }
                            />
                            <ReasonInput reasons={ReasonOptions} />
                        </div>
                    }
                />
            )}
        </Formik>
    );
};

ResubmitClaimDialog.Title = Title;
